<template>
    <div class="work-orders-list">
        <busy-modal :show="busy" :overlay="true"></busy-modal>
        <grid
            ref="grid"
            :columnDefs="columnDefs"
            :fetchCallback="fetchCallback"
            :pinnedBottomRowData="pinnedBottomRowData"
            @on-total-records="setTotalRecords"
            :getRowStyle="rowStyle"
            :autoWidth="true"
        >
            <date-range-picker
                :range-options="predefinedRanges"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="onDaterangeChange"
            ></date-range-picker>
            <b-button-group class="d-none d-lg-flex">
                <search-multiselect
                    v-model="selectedGridProperties"
                    :options="gridProperties"
                    :icon="['fas', 'hotel']"
                    class="mr-1"
                    variant="primary"
                    placeholder="Properties"
                    controlValue="Properties"
                ></search-multiselect>
            </b-button-group>
            <responsive-dropdown
                :icon="['fas', 'filter']"
                :items="filterByDateOptions"
                variant="primary"
                class="d-none d-xl-flex"
                >{{ filterByDate.label }}</responsive-dropdown
            >
            <b-button-group :style="{ width: '120px' }" class="d-none d-xl-block mr-1">
                <form-square-switch
                    v-model="displayClosedOrders"
                    @change="onDisplayClosedOrdersChange"
                    :labels="{
                        checked: 'Show Closed',
                        unchecked: 'Show Closed',
                    }"
                    :is-toolbar="true"
                ></form-square-switch>
            </b-button-group>
            <b-button-group aria-label="Export table data">
                <export-to-excel-button :href="exportExcelHref" />
            </b-button-group>
            <responsive-button :icon="['fas', 'arrows-rotate']" @click="refresh" variant="primary"
                >Refresh</responsive-button
            >
            <responsive-button
                v-if="canAdd"
                :icon="['fas', 'plus']"
                variant="primary"
                @click="onCreate"
            ></responsive-button>
            <b-button-group class="d-xl-none">
                <more-filters-dropdown>
                    <template v-slot:default="{ handleDropdownShow, handleDropdownHide }">
                        <responsive-dropdown
                            id="work-orders-filter-by-date"
                            :icon="['fas', 'filter']"
                            :items="filterByDateOptions"
                            :responsiveness="false"
                            variant="primary"
                            @show="handleDropdownShow"
                            @hide="handleDropdownHide"
                            >{{ filterByDate.label }}</responsive-dropdown
                        >
                        <search-multiselect
                            v-model="selectedGridProperties"
                            :options="gridProperties"
                            :icon="['fas', 'hotel']"
                            variant="primary"
                            placeholder="Properties"
                            controlValue="Properties"
                            class="d-flex d-lg-none"
                            @show="handleDropdownShow"
                            @hide="handleDropdownHide"
                        ></search-multiselect>
                        <form-square-switch
                            v-model="displayClosedOrders"
                            @change="onDisplayClosedOrdersChange"
                            :labels="{
                                checked: 'Show Closed',
                                unchecked: 'Show Closed',
                            }"
                        ></form-square-switch>
                    </template>
                </more-filters-dropdown>
            </b-button-group>
        </grid>
        <work-orders-modal
            :show="isModalShown"
            :modal-busy="workOrdersModalBusy"
            :canEdit="canEditWorkOrder"
            :hasModifyAccess="hasModifyAccess"
            :actionType="modalActionType"
            :workOrderToEditNumber="workOrderToEditNumber"
            :has-published-owner-statement="workOrderHasPublishedOwnerStatement"
            @workOrderSave="onWorkOrderSave"
            @modalClose="onModalClose"
        ></work-orders-modal>
    </div>
</template>

<script>
    import format from 'date-fns/format';
    import parseISO from 'date-fns/parseISO';
    import cloneDeep from 'lodash/cloneDeep';
    import { mapGetters, mapActions } from 'vuex';
    import { BVFormBtnLabelControl } from 'bootstrap-vue/src/components/form-btn-label-control/bv-form-btn-label-control';
    import API from '../../../api';
    import Grid from '../../generic/grid/grid.vue';
    import ExportToExcelButton from '../../generic/export-to-excel-button/export-to-excel-button.vue';
    import Page from '../../generic/page/page.vue';
    import Modal from '../../generic/modal/modal.vue';
    import BusyModal from '../../generic/busy-modal/busy-modal.vue';
    import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
    import ResponsiveDropdown from '../../generic/responsive-dropdown/responsive-dropdown.vue';
    import MoreFiltersDropdown from '../../generic/more-filters-dropdown/more-filters-dropdown.vue';
    import SearchMultiselect from '../../generic/search-multiselect/search-multiselect.vue';
    import DateRangePicker, { nameToRange, ALL_DATES } from '../../generic/date-range-picker/date-range-picker.vue';
    import FormSquareSwitch from '../../generic/form-square-switch/form-square-switch.vue';
    import SourceCellRenderer from './source-cell-renderer.vue';
    import WorkOrdersModal from './work-orders-modal.vue';
    import WorkOrdersActionCellRenderer from './work-orders-action-cell-renderer.vue';
    import { DEFAULT_WORK_ORDER, ActorType, WorkOrderActionType } from '../../../constants';
    import { REPORT_PROPERTIES_ALL } from '../../../endpoints';
    import { escapeHTML } from '../../../utils/strings';

    const MODAL_TIMEOUT = 200;
    const FILTER_BY_COMPLETED_DATE = 1;
    const FILTER_BY_REPORTED_DATE = 2;
    const CellRenderer = (params) => {
        return params.node.rowPinned
            ? ''
            : `<span style="{ 'font-weight': ${params.data.is_viewed} ? 0 : 600 }">${escapeHTML(params.value)}</span>`;
    };

    export default {
        name: 'WorkOrdersList',
        components: {
            Grid,
            Page,
            Modal,
            ResponsiveButton,
            ResponsiveDropdown,
            MoreFiltersDropdown,
            DateRangePicker,
            WorkOrdersModal,
            FormSquareSwitch,
            SearchMultiselect,
            BVFormBtnLabelControl,
            BusyModal,
            sourceCellRenderer: SourceCellRenderer,
            workOrdersActionCellRenderer: WorkOrdersActionCellRenderer,
            exportToExcelButton: ExportToExcelButton,
        },
        data() {
            return {
                modalActionType: WorkOrderActionType.Create,
                workOrderToEditNumber: '',
                workOrderDto: null,
                workOrdersModalBusy: false,
                workOrderHasPublishedOwnerStatement: false,
                canEditWorkOrder: true,
                hasModifyAccess: true,
                displayClosedOrders: false,
                isModalShown: false,
                busy: false,
                selectedGridProperties: [],
                gridProperties: [],
                filterByDate: this.getFilterByReportedDate(),
                offset: 0,
                limit: 20,
                sort: 'created',
                desc: false,
                search: '',
                dateRequestFormat: 'yyyy-MM-dd',
                dateRange: nameToRange(ALL_DATES),
                totalRecords: 0,
                rowStyle: null,
                predefinedRanges: [
                    'today',
                    'yesterday',
                    'this month',
                    'last month',
                    'this year',
                    'last year',
                    'all dates',
                ],
                columnDefs: [
                    {
                        colId: 'source',
                        headerName: '',
                        cellRenderer: 'sourceCellRenderer',
                        minWidth: 56,
                        maxWidth: 56,
                    },
                    {
                        headerName: '#',
                        field: 'id',
                        minWidth: 80,
                        maxWidth: 90,
                        sortable: false,
                        valueGetter: (params) => {
                            return params.node.rowPinned ? this.totalRecords : params.node.rowIndex + 1;
                        },
                    },
                    {
                        field: 'number',
                        headerName: 'ID',
                        sort: 'desc',
                        maxWidth: 110,
                        cellRenderer: CellRenderer,
                    },
                    {
                        field: 'status_name',
                        headerName: 'Status',
                        maxWidth: 100,
                        cellRenderer: CellRenderer,
                    },
                    {
                        field: 'priority_name',
                        headerName: 'Priority',
                        maxWidth: 100,
                        cellRenderer: CellRenderer,
                    },
                    {
                        field: 'property_name',
                        headerName: 'Property',
                        flex: 1,
                        cellRenderer: CellRenderer,
                    },
                    {
                        field: 'reporter_name',
                        headerName: 'Reported By',
                        flex: 1,
                        cellRenderer: CellRenderer,
                    },
                    {
                        field: 'description',
                        headerName: 'Description',
                        flex: 0,
                        cellRenderer: CellRenderer,
                    },
                    {
                        field: 'assigned_to',
                        headerName: 'Assigned To',
                        flex: 0,
                        cellRenderer: CellRenderer,
                    },
                    {
                        field: 'who_will_pay',
                        headerName: 'Who Will Pay',
                        minWidth: 140,
                        cellRenderer: CellRenderer,
                    },
                    {
                        field: 'date_reported',
                        headerName: 'Date Reported',
                        type: 'dateColumn',
                        minWidth: 126,
                    },
                    {
                        field: 'start_date',
                        headerName: 'Start Date',
                        type: 'dateColumn',
                        minWidth: 120,
                    },
                    {
                        field: 'completed_date',
                        headerName: 'Date Completed',
                        type: 'dateColumn',
                        minWidth: 136,
                    },
                    {
                        field: 'amount',
                        headerName: 'Amount',
                        maxWidth: 120,
                        cellRenderer: CellRenderer,
                    },
                    {
                        headerName: '',
                        minWidth: 190,
                        maxWidth: 190,
                        sortable: false,
                        cellStyle: { 'align-items': 'flex-end' },
                        cellRenderer: 'workOrdersActionCellRenderer',
                        cellRendererParams: {
                            modalData: {
                                openEditModal: this.onEdit,
                                onPrintToPdf: this.onPrintToPdf,
                                onDelete: this.onDelete,
                            },
                        },
                    },
                ],
            };
        },
        beforeMount() {
            this.rowStyle = (params) => {
                if (params.node.rowPinned) {
                    return { 'font-weight': 'bold' };
                }
            };
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat']),
            ...mapGetters('workOrders', [
                'workOrderToSave',
                'localMaterialExpenses',
                'metadata',
                'accountExpenses',
                'jobTypes',
                'vendors',
            ]),
            workOrderQuery() {
                return this.$route.query.work_order_id;
            },
            fetchCallback() {
                return ({ offset, limit, sort, desc, search }) => {
                    this.offset = offset;
                    this.limit = limit;
                    this.sort = sort;
                    this.desc = desc;
                    this.search = search;

                    return API.get(
                        encodeURI(
                            `/app/work-orders/api/orders/grid/?${
                                this.filterByDate.value === FILTER_BY_COMPLETED_DATE
                                    ? 'completed_start_date'
                                    : 'reported_start_date'
                            }=${format(this.dateRange.from, this.dateRequestFormat)}&${
                                this.filterByDate.value === FILTER_BY_COMPLETED_DATE
                                    ? 'completed_end_date'
                                    : 'reported_end_date'
                            }=${format(this.dateRange.to, this.dateRequestFormat)}&show_closed=${+this
                                .displayClosedOrders}&properties=${this.selectedGridProperties.join(',')}`
                        ),
                        {
                            params: {
                                offset,
                                limit,
                                sort,
                                desc,
                                search,
                            },
                        }
                    );
                };
            },
            exportExcelHref() {
                return encodeURI(
                    `/app/work-orders/api/orders/excel/?${
                        this.filterByDate.value === FILTER_BY_COMPLETED_DATE
                            ? 'completed_start_date'
                            : 'reported_start_date'
                    }=${format(this.dateRange.from, this.dateRequestFormat)}&${
                        this.filterByDate.value === FILTER_BY_COMPLETED_DATE
                            ? 'completed_end_date'
                            : 'reported_end_date'
                    }=${format(this.dateRange.to, this.dateRequestFormat)}&show_closed=${+this
                        .displayClosedOrders}&properties=${this.selectedGridProperties.join(',')}&offset=${
                        this.offset
                    }&limit=${this.limit}&sort=${this.sort}&desc=${this.desc}&search=${this.search}`
                );
            },
            canAdd() {
                return this.metadata.permissions && this.metadata.permissions.can_add;
            },
            filterByDateOptions() {
                const filterByCompletedDate = this.getFilterByCompletedDate();
                const filterByReportedDate = this.getFilterByReportedDate();

                return [
                    {
                        icon: ['fas', 'filter'],
                        label: filterByCompletedDate.label,
                        active: this.filterByDate.value === filterByCompletedDate.value,
                        click: () => {
                            this.filterByDate = filterByCompletedDate;
                            this.refresh();
                        },
                    },
                    {
                        icon: ['fas', 'filter'],
                        label: filterByReportedDate.label,
                        active: this.filterByDate.value === filterByReportedDate.value,
                        click: () => {
                            this.filterByDate = filterByReportedDate;
                            this.refresh();
                        },
                    },
                ];
            },
            pinnedBottomRowData() {
                return [
                    {
                        id: this.totalRecords,
                    },
                ];
            },
        },
        methods: {
            ...mapActions('workOrders', [
                'fetchJobTypes',
                'fetchAccountExpenses',
                'fetchFormOptions',
                'fetchTeamMembers',
                'fetchVendors',
                'fetchFormProperties',
                'fetchPropertyOwners',
                'updateWorkOrderToSave',
                'updateLocalMaterialExpenses',
            ]),
            getSafeDate(value) {
                if (!value) {
                    return '';
                }
                return typeof value === 'string' ? parseISO(value) : value;
            },
            getFilterByCompletedDate() {
                return {
                    label: 'Filter by Date Completed',
                    value: FILTER_BY_COMPLETED_DATE,
                };
            },
            getFilterByReportedDate() {
                return {
                    label: 'Filter by Date Reported',
                    value: FILTER_BY_REPORTED_DATE,
                };
            },
            onDaterangeChange(value) {
                this.dateRange = { ...value };
                this.refresh();
            },
            onCreate() {
                this.modalActionType = WorkOrderActionType.Create;
                this.workOrderHasPublishedOwnerStatement = false;
                this.updateWorkOrderToSave(DEFAULT_WORK_ORDER);
                this.isModalShown = true;
            },
            onEdit(workOrderItem) {
                this.modalActionType = WorkOrderActionType.Edit;
                (async () => {
                    await this.checkEditPermissions(workOrderItem.id);
                    await this.markViewed(workOrderItem.id);
                    await this.fetchWorkOrder(workOrderItem.id);
                    if (this.workOrderDto) {
                        this.workOrderHasPublishedOwnerStatement = this.workOrderDto.has_published_owner_statement;
                        const wo = this.mapWorkOrderDto(this.workOrderDto);
                        await this.getPropertyOwners(wo.rentalProperty.id);
                        this.updateWorkOrderToSave(cloneDeep(wo));
                        this.workOrderToEditNumber = `${wo.number}`;
                        this.isModalShown = true;
                    }
                })();
            },
            onModalClose() {
                this.isModalShown = false;
                setTimeout((_) => {
                    this.workOrderToEditNumber = '';
                    this.updateLocalMaterialExpenses([]);
                    this.updateWorkOrderToSave(DEFAULT_WORK_ORDER);
                    this.canEditWorkOrder = true;
                    this.hasModifyAccess = true;
                }, MODAL_TIMEOUT);
                this.refresh();
            },
            mapWorkOrderDto(dto = {}) {
                /**
                 * Rental Property: positive
                 * Room: negative
                 * Other: 0
                 */
                const mapProperty = (dto) => {
                    let id = 0;
                    if (dto.room) {
                        id = -Number(dto.room);
                    } else if (dto.rental_property) {
                        id = Number(dto.rental_property);
                    }

                    return {
                        id: id,
                        other: dto.rental_property_other || '',
                    };
                };

                const mapReporter = (dto) => {
                    const reporterValue =
                        dto.reporter_type === ActorType.Other && !dto.reporter ? dto.alt_reporter : dto.reporter;

                    return {
                        type: dto.reporter_type || ActorType.Subscriber,
                        value: reporterValue,
                    };
                };

                const mapExpenseAccount = (expenseId) => {
                    const hasExpenseAccount = (id) => {
                        return this.accountExpenses.map((item) => item.id).includes(id);
                    };

                    if (!expenseId || !hasExpenseAccount(expenseId)) {
                        expenseId = this.accountExpenses[0] && this.accountExpenses[0].id;
                    }

                    return expenseId;
                };

                const mapJobTypes = (jobTypeId) => {
                    const hasJobType = (id) => {
                        return this.jobTypes.map((item) => item.id).includes(id);
                    };

                    if (!jobTypeId || !hasJobType(jobTypeId)) {
                        jobTypeId = this.jobTypes[0] && this.jobTypes[0].id;
                    }

                    return jobTypeId;
                };

                const getVendorOrDefault = (vendorId = '') => {
                    return this.vendors.filter((vendor) => vendor.id === vendorId).length ? vendorId : '';
                };

                const mapAssignee = (dto) => {
                    let assigneeId = dto.assigned_to_id || '';
                    if (dto.assigned_to_type === ActorType.Vendor) {
                        assigneeId = getVendorOrDefault(assigneeId);
                    }

                    return {
                        type: dto.assigned_to_type || '',
                        value: assigneeId,
                    };
                };

                const mapNotifier = (dto) => {
                    let notifierId = dto.notify_to_id || '';
                    if (dto.notify_to_type === ActorType.Vendor) {
                        notifierId = getVendorOrDefault(notifierId);
                    }

                    return {
                        type: dto.notify_to_type || '',
                        value: notifierId,
                    };
                };

                return {
                    id: dto.id,
                    number: dto.number || '',
                    status: dto.status,
                    rentalProperty: mapProperty(dto),
                    altReporter: dto.alt_reporter || '',
                    reporter: mapReporter(dto),
                    accountAssignee:
                        // subscriber: current logged in user
                        dto.who_will_pay === ActorType.Subscriber ? ActorType.Subscriber : dto.account_assignee,
                    whoWillPay: dto.who_will_pay || '',
                    account: mapExpenseAccount(dto.account),
                    jobType: mapJobTypes(dto.job_type),
                    priority: dto.priority,
                    dateReported: dto.date_reported
                        ? format(parseISO(dto.date_reported), 'yyyy-MM-dd')
                        : format(new Date(), 'yyyy-MM-dd'),
                    description: dto.description || '',
                    startDate: dto.start_date ? format(parseISO(dto.start_date), 'yyyy-MM-dd') : null,
                    completedDate: dto.completed_date ? format(parseISO(dto.completed_date), 'yyyy-MM-dd') : null,
                    amount: dto.amount || 0,
                    invoiceNumber: dto.invoice_number || '',
                    invoiceVendor: getVendorOrDefault(dto.invoice_vendor),
                    receiptNumber: dto.receipt_number || '',
                    receiptStore: getVendorOrDefault(dto.receipt_store),
                    assignee: mapAssignee(dto),
                    notifier: mapNotifier(dto),
                    emailDetails: dto.email_details,
                    notes: dto.notes || '',
                    internalNotes: dto.vendor_team_member_notes || '',
                    photos: dto.photos || [],
                    damage_notice_source: dto.damage_notice_source || null,
                };
            },
            mapWorkOrderPayload() {
                const wo = cloneDeep(this.workOrderToSave);
                const dateReported = this.getSafeDate(wo.dateReported);
                const startDate = this.getSafeDate(wo.startDate);
                const completedDate = this.getSafeDate(wo.completedDate);
                const formData = new FormData();

                const data = {
                    id: wo.id,
                    number: wo.number,
                    status: wo.status,
                    // property
                    property_or_room: wo.rentalProperty.id,
                    alt_rental_property: wo.rentalProperty.other,
                    // reporter
                    reported_by_type: wo.reporter.type,
                    reporter_team_member:
                        wo.reporter.type === ActorType.TeamMember || wo.reporter.type === ActorType.Subscriber
                            ? wo.reporter.value
                            : '',
                    reporter_owner: wo.reporter.type === ActorType.Owner ? wo.reporter.value : '',
                    alt_reporter: wo.reporter.type === ActorType.Other ? wo.reporter.value : '',
                    // expense account
                    account: wo.account,
                    job_type: wo.jobType,
                    priority: wo.priority,
                    date_reported: dateReported ? format(dateReported, this.userUnicodeDateFormat) : '',
                    description: wo.description,
                    start_date: startDate ? format(startDate, this.userUnicodeDateFormat) : '',
                    completed_date: completedDate ? format(completedDate, this.userUnicodeDateFormat) : '',
                    amount: wo.amount,
                    invoice_number: wo.invoiceNumber,
                    receipt_number: wo.receiptNumber,
                    invoice_vendor: wo.invoiceVendor,
                    receipt_store: wo.receiptStore,
                    // assignee
                    assigned_to: wo.assignee.type,
                    team_member:
                        wo.assignee.type === ActorType.TeamMember || wo.assignee.type === ActorType.Owner
                            ? wo.assignee.value
                            : '',
                    team_member_type:
                        wo.assignee.type === ActorType.TeamMember || wo.assignee.type === ActorType.Owner
                            ? wo.assignee.type
                            : '',
                    vendor: wo.assignee.type === ActorType.Vendor ? wo.assignee.value : '',
                    // notifier
                    also_notify: wo.notifier.type,
                    also_notify_team_member:
                        wo.notifier.type === ActorType.TeamMember || wo.notifier.type === ActorType.Owner
                            ? wo.notifier.value
                            : '',
                    also_notify_vendor: wo.notifier.type === ActorType.Vendor ? wo.notifier.value : '',
                    email_details: wo.emailDetails,
                    notes: wo.notes,
                    vendor_team_member_notes: wo.internalNotes,
                    // 'SUBSCRIBER' or Owner ID
                    account_assignee: wo.accountAssignee,
                    material_expenses:
                        this.modalActionType === WorkOrderActionType.Edit ? [] : [...this.localMaterialExpenses],
                    photos: wo.photos,
                };
                formData.append('id', data.id);
                if (data.number) {
                    formData.append('number', data.number);
                }
                formData.append('status', data.status);
                formData.append('property_or_room', data.property_or_room);
                formData.append('alt_rental_property', data.alt_rental_property);
                formData.append('reported_by_type', data.reported_by_type);
                formData.append('reporter_team_member', data.reporter_team_member);
                formData.append('reporter_owner', data.reporter_owner);
                formData.append('alt_reporter', data.alt_reporter);
                formData.append('account', data.account);
                formData.append('job_type', data.job_type);
                formData.append('priority', data.priority);
                formData.append('date_reported', data.date_reported);
                formData.append('description', data.description);
                formData.append('start_date', data.start_date);
                formData.append('completed_date', data.completed_date);
                formData.append('amount', data.amount);
                formData.append('invoice_number', data.invoice_number);
                formData.append('receipt_number', data.receipt_number);
                formData.append('invoice_vendor', data.invoice_vendor);
                formData.append('receipt_store', data.receipt_store);
                formData.append('assigned_to', data.assigned_to);
                formData.append('team_member', data.team_member);
                formData.append('team_member_type', data.team_member_type);
                formData.append('vendor', data.vendor);
                formData.append('also_notify', data.also_notify);
                formData.append('also_notify_team_member', data.also_notify_team_member);
                formData.append('also_notify_vendor', data.also_notify_vendor);
                formData.append('email_details', data.email_details);
                formData.append('notes', data.notes);
                formData.append('vendor_team_member_notes', data.vendor_team_member_notes);
                formData.append('account_assignee', data.account_assignee);
                data.material_expenses.forEach((materialExpense) => {
                    formData.append('material_expense_dates', materialExpense.date);
                    formData.append('material_expense_descriptions', materialExpense.description);
                    formData.append('material_expense_qties', materialExpense.qty);
                    formData.append('material_expense_prices', materialExpense.price);
                    formData.append('material_expense_ids', materialExpense.id);
                });
                data.photos
                    .filter((photo) => !photo.deleted)
                    .forEach((photo) => {
                        if (photo.id === undefined) {
                            formData.append('photos_to_create', photo.file);
                            formData.append('photo_captions_to_create', photo.caption);
                        } else {
                            formData.append('photo_ids_to_update', photo.id);
                            formData.append('photo_captions_to_update', photo.caption);
                        }
                    });
                data.photos
                    .filter((photo) => photo.deleted && photo.id)
                    .forEach((photo) => {
                        formData.append('photo_ids_to_delete', photo.id);
                    });
                return formData;
            },
            refresh() {
                this.$refs.grid.refreshDataSource();
            },
            onPrintToPdf(workOrderItem) {
                return `/app/work-orders/api/orders/pdf/?pk=${workOrderItem.id}`;
            },
            onDisplayClosedOrdersChange(event) {
                this.displayClosedOrders = event.value;
                this.refresh();
            },
            async onWorkOrderSave() {
                if (this.modalActionType === WorkOrderActionType.Edit) {
                    this.updateWorkOrder(this.mapWorkOrderPayload());
                }
                if (this.modalActionType === WorkOrderActionType.Create) {
                    this.createWorkOrder(this.mapWorkOrderPayload());
                }
            },
            async getPropertyOwners(id) {
                await this.dispatchRequest(
                    async (_) =>
                        this.fetchPropertyOwners({
                            id: id,
                        }),
                    'Could not load property owners.',
                    true
                );
            },
            async loadGridProperties() {
                await this.dispatchRequest(async (_) => {
                    const properties = await API.get(REPORT_PROPERTIES_ALL());

                    properties.data.forEach((property) => {
                        this.gridProperties.push({
                            value: property.id,
                            text: property.name,
                            disabled: property.disabled,
                        });
                    });
                }, `Could not load grid properties.`);
            },
            async markViewed(id) {
                await this.dispatchRequest(
                    async (_) =>
                        API.post(`/app/work-orders/api/orders/mark-viewed/`, {
                            wo_pk: id,
                        }),
                    `Could not mark work order as viewed.`
                );
            },
            async fetchWorkOrder(id) {
                await this.dispatchRequest(
                    async (_) => {
                        const response = await API.get(`/app/work-orders/api/orders/${id}/`);
                        this.workOrderDto = response.data;
                    },
                    `Could not load work order ${id}.`,
                    true
                );
            },
            async checkEditPermissions(id) {
                await this.dispatchRequest(async (_) => {
                    const response = await API.get(`/app/work-orders/metadata/?wo_pk=${id}`);
                    const data = response.data;
                    if (data && data.permissions) {
                        this.canEditWorkOrder = !data.permissions.is_modify_disabled;
                        this.hasModifyAccess = data.permissions.has_modify_access;
                    }
                }, `Could not load edit permissions.`);
            },
            async onDelete(workOrderItem) {
                await this.dispatchRequest(
                    (_) => API.delete(`/app/work-orders/api/orders/${workOrderItem.id}/`),
                    `Could not delete work order ${workOrderItem.id}.`
                );
                this.refresh();
            },
            async createWorkOrder(workOrderToSave) {
                await this.dispatchRequest(async (_) => {
                    try {
                        this.workOrdersModalBusy = true;
                        await API.post(`/app/work-orders/api/orders/`, workOrderToSave, {
                            headers: {
                                'Content-Type': 'multipart/form-data',
                            },
                        });
                        this.isModalShown = false;
                    } finally {
                        this.workOrdersModalBusy = false;
                    }
                }, `Could not create work order.`);
                this.refresh();
            },
            async updateWorkOrder(workOrderToSave) {
                await this.dispatchRequest(async (_) => {
                    try {
                        this.workOrdersModalBusy = true;
                        await API.patch(`/app/work-orders/api/orders/${workOrderToSave.get('id')}/`, workOrderToSave, {
                            headers: {
                                'Content-Type': 'multipart/form-data',
                            },
                        });
                        this.isModalShown = false;
                    } finally {
                        this.workOrdersModalBusy = false;
                    }
                }, `Could not update work order ${workOrderToSave.get('id')}.`);
                this.refresh();
            },
            async loadJobTypes() {
                await this.dispatchRequest((_) => this.fetchJobTypes(), 'Could not load job types.');
            },
            async loadAccountExpenses() {
                await this.dispatchRequest((_) => this.fetchAccountExpenses(), 'Could not load account expenses.');
            },
            async loadFormOptions() {
                await this.dispatchRequest((_) => this.fetchFormOptions(), 'Could not load form options.');
            },
            async loadFormProperties() {
                await this.dispatchRequest((_) => this.fetchFormProperties(), 'Could not load form properties.');
            },
            async loadTeamMembers() {
                await this.dispatchRequest((_) => this.fetchTeamMembers(), 'Could not load team members.');
            },
            async loadVendors() {
                await this.dispatchRequest((_) => this.fetchVendors(), 'Could not load vendors.');
            },
            async dispatchRequest(request, errorMessage = '', withLoader = false) {
                const toggleLoader = (busy) => {
                    if (withLoader) {
                        this.busy = busy;
                    }
                };

                try {
                    toggleLoader(true);
                    await request();
                } catch (err) {
                    this.errorToast({
                        text: errorMessage + ' ' + err.response.data,
                    });
                } finally {
                    toggleLoader(false);
                }
            },
            setTotalRecords(value) {
                this.totalRecords = value;
                this.$refs.grid.gridOptions.api.setGridOption('pinnedBottomRowData', this.pinnedBottomRowData);
            },
        },
        mounted() {
            (async () => {
                this.busy = true;
                await Promise.all([
                    this.loadGridProperties(),
                    this.loadFormProperties(),
                    this.loadTeamMembers(),
                    this.loadVendors(),
                    this.loadJobTypes(),
                    this.loadAccountExpenses(),
                    this.loadFormOptions(),
                ]);
                this.busy = false;
                if (this.$router.currentRoute.params?.fromVendorGrid) {
                    this.onCreate();
                }
                if (this.workOrderQuery) {
                    this.onEdit({ id: this.workOrderQuery });
                }
            })();
        },
        watch: {
            selectedGridProperties(properties) {
                this.refresh();
            },
        },
    };
</script>

<style scoped lang="scss">
    .work-orders-list {
        height: 100%;
    }
</style>
